import axios from 'axios';

const M = '/api/method';

const base = (import.meta.env.VITE_API_BASE_URL && import.meta.env.VITE_API_BASE_URL.trim()) || window.location.origin;

// Create axios instance with base configuration
const api = axios.create({
  baseURL: base,
  timeout: 30000,
});

// Token refresh management
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function subscribeToTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

function onTokenRefreshed(newToken: string) {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
}

// Check if token expires soon (within 5 minutes)
function isTokenExpiringSoon(): boolean {
  const expiresAt = localStorage.getItem('auth_token_expires_at');
  if (!expiresAt) return false;

  const expiresDate = new Date(expiresAt);
  const now = new Date();
  const fiveMinutes = 5 * 60 * 1000;

  return (expiresDate.getTime() - now.getTime()) < fiveMinutes;
}

// Refresh the access token using the refresh token
async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await axios.post(
      `${base}${M}/ski_delivery.api._auth_resort_admin.refresh_token`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const data = response.data.message || response.data;
    if (data.jwt) {
      // Update stored tokens
      localStorage.setItem('auth_token', data.jwt);
      localStorage.setItem('auth_token_expires_at', new Date(Date.now() + data.expires_in * 1000).toISOString());
      localStorage.setItem('refresh_token', data.refresh_token);
      localStorage.setItem('refresh_token_expires_at', new Date(Date.now() + data.refresh_token_expires_in * 1000).toISOString());
      return data.jwt;
    }
    return null;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return null;
  }
}

// Add request interceptor to include auth token if available
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('auth_token');
    if (token && config.headers) {
      // Check if token is expiring soon and we have a refresh token
      const refreshToken = localStorage.getItem('refresh_token');
      if (isTokenExpiringSoon() && refreshToken && !isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();
          if (newToken) {
            config.headers['X-Resort-Admin-Auth'] = `Bearer ${newToken}`;
            onTokenRefreshed(newToken);
          } else {
            config.headers['X-Resort-Admin-Auth'] = `Bearer ${token}`;
          }
        } finally {
          isRefreshing = false;
        }
      } else if (isRefreshing) {
        // Another request is refreshing the token, wait for it
        return new Promise(resolve => {
          subscribeToTokenRefresh((newToken: string) => {
            config.headers!['X-Resort-Admin-Auth'] = `Bearer ${newToken}`;
            resolve(config);
          });
        });
      } else {
        config.headers['X-Resort-Admin-Auth'] = `Bearer ${token}`;
      }
    }
    // JSON body for Frappe is fine, but don't override FormData
    if (config.method !== 'get' && config.headers && !(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Basic error normalization with silent token refresh
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const originalRequest = error.config;
    const status = error?.response?.status;
    const data = error?.response?.data;

    // Helper function to check if error message indicates token expiration
    const isTokenExpiredError = (message: string): boolean => {
      const lowerMsg = message.toLowerCase();
      return lowerMsg.includes('token has expired') ||
             lowerMsg.includes('token expired') ||
             lowerMsg.includes('missing bearer token') ||
             lowerMsg.includes('invalid token');
    };

    // Helper function to handle auth expiration
    const handleAuthExpired = () => {
      try {
        sessionStorage.setItem('auth_expired', '1');
        try {
          const currentPath = window.location.pathname + window.location.search + window.location.hash;
          sessionStorage.setItem('auth_expired_path', currentPath);
        } catch {}
      } catch {}
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_token_expires_at');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('refresh_token_expires_at');
      localStorage.removeItem('user');
      window.dispatchEvent(new CustomEvent('auth-expired'));
    };

    // Check if this is a 401 error and we haven't tried to refresh yet
    if (status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('refresh_token');

      // Only attempt refresh if we have a refresh token
      if (refreshToken) {
        originalRequest._retry = true;

        // If already refreshing, wait for it
        if (isRefreshing) {
          return new Promise(resolve => {
            subscribeToTokenRefresh((newToken: string) => {
              originalRequest.headers['X-Resort-Admin-Auth'] = `Bearer ${newToken}`;
              resolve(api(originalRequest));
            });
          });
        }

        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();
          if (newToken) {
            onTokenRefreshed(newToken);
            originalRequest.headers['X-Resort-Admin-Auth'] = `Bearer ${newToken}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Refresh failed or no refresh token, logout
      handleAuthExpired();
      return Promise.reject(new Error('Session expired. Please log in again.'));
    }

    // First, check for Frappe _server_messages (validation errors) - handle before auth check
    // This prevents validation errors from being treated as auth failures
    if (data?._server_messages) {
      try {
        const serverMessages = JSON.parse(data._server_messages);
        if (Array.isArray(serverMessages) && serverMessages.length > 0) {
          // Each message is also JSON-encoded
          const firstMsg = JSON.parse(serverMessages[0]);
          if (firstMsg.message) {
            // Check if this is a token expiration error
            if (isTokenExpiredError(firstMsg.message)) {
              handleAuthExpired();
              return Promise.reject(new Error('Authentication expired. Please log in again.'));
            }
            return Promise.reject(new Error(firstMsg.message));
          }
        }
      } catch (e) {
        // If parsing fails, continue to other handlers
      }
    }

    // Check for exc_type to identify Frappe validation errors
    if (data?.exc_type === 'ValidationError') {
      const msg = data?._error_message || data?.exception || 'Validation error';
      // Check if this is a token expiration error
      if (isTokenExpiredError(msg)) {
        handleAuthExpired();
        return Promise.reject(new Error('Authentication expired. Please log in again.'));
      }
      return Promise.reject(new Error(msg));
    }

    // Treat 403/417 as authentication/authorization failures (401 handled above with refresh)
    if (status === 403 || status === 417) {
      handleAuthExpired();
      return Promise.reject(new Error('Authentication expired. Please log in again.'));
    }

    // Handle 422 validation errors with better message extraction
    if (status === 422) {
      // Try multiple possible error message locations
      if (data?._error_message) {
        return Promise.reject(new Error(data._error_message));
      }
      if (data?.exception) {
        return Promise.reject(new Error(data.exception));
      }
      if (data?.message) {
        return Promise.reject(new Error(data.message));
      }
      // For Pydantic validation errors, try to extract field-specific messages
      if (data?.detail && Array.isArray(data.detail)) {
        const messages = data.detail.map((err: any) => {
          const field = err.loc?.join('.') || 'field';
          const msg = err.msg || 'Invalid value';
          return `${field}: ${msg}`;
        });
        return Promise.reject(new Error(messages.join('; ')));
      }
      if (data?.detail && typeof data.detail === 'string') {
        return Promise.reject(new Error(data.detail));
      }
    }

    if (data?.exception || data?._error_message) {
      const msg = data._error_message || data.exception;
      return Promise.reject(new Error(msg));
    }
    return Promise.reject(error);
  }
);

// Auth
export async function login(email: string, password: string, deviceId?: string) {
  const payload: any = { email, password };
  if (deviceId) {
    payload.device_id = deviceId;
  }
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.login`, payload);
  return data.message || data;
}

export async function refreshToken(token: string) {
  const { data } = await api.post(`${M}/ski_delivery.api._auth_resort_admin.refresh_token`, { refresh_token: token });
  return data.message || data;
}

export async function logout(token?: string, allDevices: boolean = false) {
  const payload: any = { all_devices: allDevices };
  if (token) {
    payload.refresh_token = token;
  }
  const { data } = await api.post(`${M}/ski_delivery.api._auth_resort_admin.logout`, payload);
  return data.message || data;
}

export async function whoami() {
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.whoami`);
  return data.message || data;
}

// Resort Admin Users
export async function listResortAdminUsers(q?: string, scope_type?: string, page = 1, pageSize = 50) {
  const params = new URLSearchParams();
  if (q) params.append('q', q);
  if (scope_type) params.append('scope_type', scope_type);
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.list_resort_admin_users?${params.toString()}`);
  return data.message || data;
}

export async function getResortAdminUser(name: string) {
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.get_resort_admin_user?name=${encodeURIComponent(name)}`);
  return data.message || data;
}

export async function createResortAdminUser(payload: {
  email: string;
  role: string; // 'Partner Admin' | 'Partner Courier' | 'Support'
  scope_type: 'restaurant' | 'grocery';
  restaurants?: string[];
  groceries?: string[];
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.create_resort_admin_user`, payload);
  return data.message || data;
}

export async function verifyPasswordToken(token: string) {
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.verify_password_token?token=${encodeURIComponent(token)}`);
  return data.message || data;
}

export async function setPassword(token: string, newPassword: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.set_password`, { token, new_password: newPassword });
  return data.message || data;
}

export async function updateResortAdminUser(payload: {
  name: string;
  password?: string;
  status?: 'Active' | 'Disabled';
  role?: string; // 'Resort Super Admin' | 'Partner Admin' | 'Partner Courier' | 'Support'
  scope_type?: 'restaurant' | 'grocery';
  restaurants?: string[];
  groceries?: string[];
}) {
  const { name, ...body } = payload;
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.update_resort_admin_user?name=${encodeURIComponent(name)}`, body);
  return data.message || data;
}

export async function deleteResortAdminUser(name: string) {
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.delete_resort_admin_user?name=${encodeURIComponent(name)}`);
  return data.message || data;
}

export async function listResortAdminRoles() {
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.list_resort_admin_roles`);
  return data.message || data;
}

export async function resendPasswordToken(userName: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.resend_password_token`, { user_name: userName });
  return data.message || data;
}

export async function changeOwnPassword(currentPassword: string, newPassword: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.change_own_password`, {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return data.message || data;
}

export async function forgotPassword(email: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.forgot_password`, { email });
  return data.message || data;
}

export async function confirmEmail(token: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.confirm_email`, { token });
  return data.message || data;
}

// Restaurants
export async function listRestaurants(q?: string, page = 1, pageSize = 50) {
  const params = new URLSearchParams();
  if (q) params.append('q', q);
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.list_restaurants?${params.toString()}`);
  return data.message || data;
}

export async function createRestaurant(payload: {
  resort_location: string;
  restaurant_name: string;
  restaurant_description: string;
  restaurant_coordinates: string;
  restaurant_type: string;
  restaurant_address: string;
  working_timetable: object;
  restaurant_code?: string;
  food_type_tags?: string[];
  delivery_zone_polygon?: { lat: number; lng: number }[];
  est_delivery_time?: number;
  click_and_collect?: number;
  pricing_tier: string;
  min_order_value?: number;
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.create_restaurant`, payload);
  return data.message || data;
}

export async function createRestaurantWithAdmin(payload: {
  resort_location: string;
  restaurant_name: string;
  restaurant_description: string;
  restaurant_coordinates: string;
  restaurant_type: string;
  restaurant_address: string;
  working_timetable: object;
  admin_email: string;
  restaurant_code?: string;
  status?: string;
  food_type_tags?: string[];
  delivery_zone_polygon?: { lat: number; lng: number }[];
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.create_restaurant_with_admin`, payload);
  return data.message || data;
}

export async function getRestaurant(name: string) {
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.get_restaurant?name=${encodeURIComponent(name)}`);
  return data.message || data;
}

export async function updateRestaurant(payload: {
  restaurant_id: string;
  resort?: string;
  restaurant_name?: string;
  restaurant_address?: string;
  restaurant_description?: string;
  restaurant_coordinates?: string;
  restaurant_type?: string;
  restaurant_image?: string;
  food_type_tags?: string[];
  working_timetable?: string;
  delivery_zone_polygon?: { lat: number; lng: number }[] | null;
  delivery_zones?: Array<{ resort: string; delivery_zone_polygon: { lat: number; lng: number }[] | null }>;
  clear_delivery_zone?: boolean;
  est_delivery_time?: number;
  pricing_tier?: string;
  off_now?: number;
  auto_reset_off_now?: number;
  min_order_value?: number;
}) {
  const { restaurant_id, ...body } = payload;
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.update_restaurant?restaurant_id=${encodeURIComponent(restaurant_id)}`, body);
  return data.message || data;
}

export async function uploadFile(file: File, restaurantId?: string): Promise<{ file_url: string; file_name: string; name: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('is_private', '0'); // Public file
  formData.append('folder', 'Home/Attachments');

  const token = localStorage.getItem('auth_token');
  const url = restaurantId 
    ? `${M}/ski_delivery.api.resort_admin.upload_file?restaurant_id=${encodeURIComponent(restaurantId)}`
    : `${M}/ski_delivery.api.resort_admin.upload_file`;
  
  const { data } = await api.post(url, formData, {
    headers: {
      'X-Resort-Admin-Auth': token ? `Bearer ${token}` : '',
      // Don't set Content-Type, let browser set it with boundary
    },
  });
  return data.message || data;
}

export async function listFoodTags(isRestaurantTag?: boolean) {
  const params: Record<string, string> = {};
  if (isRestaurantTag !== undefined) {
    params.is_restaurant_tag = isRestaurantTag.toString();
  }
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.list_food_tags`, { params });
  return data.message || data;
}

export async function getRestaurantTypeOptions() {
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.get_restaurant_type_options`);
  return data.message || data;
}

// Groceries
export async function listGroceries(q?: string, page = 1, pageSize = 50) {
  const params = new URLSearchParams();
  if (q) params.append('q', q);
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.list_groceries?${params.toString()}`);
  return data.message || data;
}

export async function createGrocery(payload: {
  resort_location: string;
  grocery_name: string;
  grocery_description?: string;
  grocery_coordinates?: string;
  grocery_type?: string;
  grocery_address?: string;
  working_timetable?: object;
  grocery_code?: string;
  grocery_food_tags?: string[];
  is_active?: number;
  delivery_zone_polygon?: { lat: number; lng: number }[];
  est_delivery_time?: number;
  click_and_collect?: number;
  pricing_tier: string;
  min_order_value?: number;
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.create_grocery`, payload);
  return data.message || data;
}

export async function createGroceryWithAdmin(payload: {
  resort_location: string;
  grocery_name: string;
  admin_email: string;
  grocery_description?: string;
  grocery_coordinates?: string;
  grocery_type?: string;
  grocery_address?: string;
  working_timetable?: object;
  grocery_code?: string;
  status?: string;
  grocery_food_tags?: string[];
  delivery_zone_polygon?: { lat: number; lng: number }[];
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.create_grocery_with_admin`, payload);
  return data.message || data;
}

export async function getGrocery(name: string) {
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.get_grocery?name=${encodeURIComponent(name)}`);
  return data.message || data;
}

export async function updateGrocery(payload: {
  grocery_id: string;
  resort_location?: string;
  grocery_name?: string;
  grocery_description?: string;
  grocery_coordinates?: string;
  grocery_type?: string;
  grocery_address?: string;
  grocery_image?: string;
  grocery_food_tags?: string[];
  working_timetable?: string;
  is_active?: number;
  notes?: string;
  delivery_zone_polygon?: { lat: number; lng: number }[] | null;
  delivery_zones?: Array<{ resort: string; delivery_zone_polygon: { lat: number; lng: number }[] | null }>;
  clear_delivery_zone?: boolean;
  est_delivery_time?: number;
  pricing_tier?: string;
  off_now?: number;
  auto_reset_off_now?: number;
  min_order_value?: number;
}) {
  const { grocery_id, ...body } = payload;
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.update_grocery?grocery_id=${encodeURIComponent(grocery_id)}`, body);
  return data.message || data;
}

export async function uploadGroceryFile(file: File, groceryId?: string): Promise<{ file_url: string; file_name: string; name: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('is_private', '0'); // Public file
  formData.append('folder', 'Home/Attachments');

  const token = localStorage.getItem('auth_token');
  const url = groceryId
    ? `${M}/ski_delivery.api.resort_admin.upload_file?grocery_id=${encodeURIComponent(groceryId)}`
    : `${M}/ski_delivery.api.resort_admin.upload_file`;

  const { data } = await api.post(url, formData, {
    headers: {
      'X-Resort-Admin-Auth': token ? `Bearer ${token}` : '',
      // Don't set Content-Type, let browser set it with boundary
    },
  });
  return data.message || data;
}

export async function listGroceryFoodTags(isGroceryTag?: boolean) {
  const params: Record<string, string> = {};
  if (isGroceryTag !== undefined) {
    params.is_grocery_tag = isGroceryTag.toString();
  }
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.list_grocery_food_tags`, { params });
  return data.message || data;
}

export async function getGroceryTypeOptions() {
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.get_grocery_type_options`);
  return data.message || data;
}

// Resort Locations (for dropdowns)
export async function getResortLocations() {
  const { data } = await api.post(`${M}/ski_delivery.api.availability.get_resort_locations`);
  return data.message || data;
}

// Resorts CRUD
export async function listResorts(q?: string, page = 1, pageSize = 50) {
  const params = new URLSearchParams();
  if (q) params.append('q', q);
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  const { data } = await api.get(`${M}/ski_delivery.api.resorts.list_resorts?${params.toString()}`);
  return data.message || data;
}

export async function getResort(name: string) {
  const { data } = await api.get(`${M}/ski_delivery.api.resorts.get_resort?name=${encodeURIComponent(name)}`);
  return data.message || data;
}

export async function createResort(payload: {
  resort_name: string;
  resort_image?: string;
  default_warehouse?: string;
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.resorts.create_resort`, payload);
  return data.message || data;
}

export async function updateResort(payload: {
  resort_id: string;
  resort_name?: string;
  resort_image?: string;
  default_warehouse?: string;
  clear_image?: boolean;
  coming_soon?: number;
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.resorts.update_resort`, payload);
  return data.message || data;
}

export async function deleteResort(resortId: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resorts.delete_resort`, { resort_id: resortId });
  return data.message || data;
}

export async function uploadResortFile(file: File, resortId?: string) {
  const formData = new FormData();
  formData.append('file', file);
  if (resortId) {
    formData.append('resort_id', resortId);
  }
  const { data } = await api.post(`${M}/ski_delivery.api.resorts.upload_resort_file`, formData);
  return data.message || data;
}

// Promo Codes
export async function listPromoCodes(
  filters?: {
    status?: string;
    discount_type?: string;
    is_redeemed?: boolean;
    start_date?: string;
    end_date?: string;
  },
  search?: string,
  page = 1,
  pageSize = 50
) {
  const params = new URLSearchParams();
  if (filters) {
    params.append('filters_json', JSON.stringify(filters));
  }
  if (search) params.append('search', search);
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  
  const { data } = await api.get(`${M}/ski_delivery.api.promo_code.admin_list_promo_codes?${params.toString()}`);
  return data.message || data;
}

export async function getPromoCode(name: string) {
  const { data } = await api.get(`${M}/ski_delivery.api.promo_code.admin_get_promo_code?name=${encodeURIComponent(name)}`);
  return data.message || data;
}

export async function createPromoCode(payload: {
  code: string;
  discount_type: 'fixed' | 'percentage';
  discount_value: number;
  start_date: string;
  end_date: string;
  min_order_amount?: number;
  max_order_amount?: number;
  restaurant?: string | null;
  max_uses_per_user?: number;
  status?: 'Active' | 'Inactive';
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.promo_code.admin_create_promo_code`, payload);
  return data.message || data;
}

export async function bulkGeneratePromoCodes(payload: {
  count: number;
  discount_type: 'fixed' | 'percentage';
  discount_value: number;
  start_date: string;
  end_date: string;
  min_order_amount?: number;
  max_order_amount?: number;
  restaurant?: string | null;
  max_uses_per_user?: number;
  status?: 'Active' | 'Inactive';
  code_prefix?: string;
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.promo_code.admin_bulk_generate_promo_codes`, payload);
  return data.message || data;
}

export async function updatePromoCode(payload: {
  name: string;
  discount_type?: 'fixed' | 'percentage';
  discount_value?: number;
  min_order_amount?: number;
  max_order_amount?: number;
  restaurant?: string | null;
  max_uses_per_user?: number;
  start_date?: string;
  end_date?: string;
  status?: 'Active' | 'Inactive';
}) {
  // Send name in body, not as query parameter (API expects UpdatePromoCodeModel with name in body)
  const { data } = await api.post(`${M}/ski_delivery.api.promo_code.admin_update_promo_code`, payload);
  return data.message || data;
}

export async function activatePromoCode(name: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.promo_code.admin_activate_promo_code`, { name });
  return data.message || data;
}

export async function deactivatePromoCode(name: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.promo_code.admin_deactivate_promo_code`, { name });
  return data.message || data;
}

// Restaurant Publish/Unpublish
export async function publishRestaurant(restaurantId: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.publish_restaurant?restaurant_id=${encodeURIComponent(restaurantId)}`);
  return data.message || data;
}

export async function unpublishRestaurant(restaurantId: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.unpublish_restaurant?restaurant_id=${encodeURIComponent(restaurantId)}`);
  return data.message || data;
}

// Grocery Publish/Unpublish
export async function publishGrocery(groceryId: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.publish_grocery?grocery_id=${encodeURIComponent(groceryId)}`);
  return data.message || data;
}

export async function unpublishGrocery(groceryId: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.unpublish_grocery?grocery_id=${encodeURIComponent(groceryId)}`);
  return data.message || data;
}

// Orders
export async function listOrders(
  filters?: {
    restaurant?: string;
    status?: string;
    min_price?: number;
    max_price?: number;
    start_date?: string;
    end_date?: string;
  },
  search?: string,
  page = 1,
  pageSize = 50
) {
  const params = new URLSearchParams();
  if (filters) {
    params.append('filters_json', JSON.stringify(filters));
  }
  if (search) params.append('search', search);
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());

  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.list_orders?${params.toString()}`);
  return data.message || data;
}

export async function getOrderStats(
  filters?: {
    restaurant?: string;
    status?: string;
    min_price?: number;
    max_price?: number;
    start_date?: string;
    end_date?: string;
  }
) {
  const params = new URLSearchParams();
  if (filters) {
    params.append('filters_json', JSON.stringify(filters));
  }

  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.get_order_stats?${params.toString()}`);
  return data.message || data;
}

export async function exportOrders(
  filters?: {
    restaurant?: string;
    status?: string;
    min_price?: number;
    max_price?: number;
    start_date?: string;
    end_date?: string;
  }
) {
  const params = new URLSearchParams();
  if (filters) {
    params.append('filters_json', JSON.stringify(filters));
  }

  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.export_orders?${params.toString()}`, {
    responseType: 'blob'
  });
  return data;
}

// Billing
export async function listBilling(
  filters?: {
    restaurant?: string;
    status?: string;
    min_price?: number;
    max_price?: number;
    start_date?: string;
    end_date?: string;
  },
  search?: string,
  page = 1,
  pageSize = 50
) {
  const params = new URLSearchParams();
  if (filters) {
    params.append('filters_json', JSON.stringify(filters));
  }
  if (search) params.append('search', search);
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());

  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.list_billing?${params.toString()}`);
  return data.message || data;
}

export async function getBillingStats(
  filters?: {
    restaurant?: string;
    status?: string;
    min_price?: number;
    max_price?: number;
    start_date?: string;
    end_date?: string;
  }
) {
  const params = new URLSearchParams();
  if (filters) {
    params.append('filters_json', JSON.stringify(filters));
  }

  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.get_billing_stats?${params.toString()}`);
  return data.message || data;
}

// Orders (Detailed)
export async function listOrdersDetailed(
  filters?: {
    restaurant?: string;
    status?: string;
    min_price?: number;
    max_price?: number;
    start_date?: string;
    end_date?: string;
  },
  search?: string,
  page = 1,
  pageSize = 50
) {
  const params = new URLSearchParams();
  if (filters) {
    params.append('filters_json', JSON.stringify(filters));
  }
  if (search) params.append('search', search);
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());

  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.list_orders_detailed?${params.toString()}`);
  return data.message || data;
}

// Cancel Order (Resort Super Admin only)
export async function cancelOrder(orderId: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.cancel_order`, { order_id: orderId });
  return data.message || data;
}

// Refund Order (Resort Super Admin only)
export async function refundOrder(orderId: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.refund_order`, { order_id: orderId });
  return data.message || data;
}

// Update Order Status (Resort Super Admin, Partner Admin, Support)
export async function updateOrderStatus(orderId: string, newStatus: string, supportNote: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.update_order_status`, {
    order_id: orderId,
    new_status: newStatus,
    support_note: supportNote,
  });
  return data.message || data;
}
